const DB_NAME = "retail-recordings";
const DB_VERSION = 1;
const CHUNKS_STORE = "chunks";
const SESSIONS_STORE = "sessions";

// How long buffered recording data may linger locally before it's considered
// abandoned and eligible for cleanup. Anything older than this is almost
// certainly a leftover from an interrupted recording that never finished
// uploading (or an old browser that predates the `createdAt` field).
const RECORDING_DATA_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type ChunkRecord = {
  id: string;
  recordingId: string;
  chunkIndex: number;
  blob: Blob;
  uploaded: boolean;
  createdAt: number;
};

type SessionRecord = {
  recordingId: string;
  contactId: string;
  mimeType: string;
  pendingComplete: boolean;
  expectedFinalChunkIndex?: number;
  createdAt: number;
};

let dbPromise: Promise<IDBDatabase> | null = null;

const attachDatabaseLifecycleHandlers = (db: IDBDatabase): void => {
  db.onversionchange = () => {
    db.close();
    dbPromise = null;
  };
  db.onclose = () => {
    dbPromise = null;
  };
};

const openDatabase = (): Promise<IDBDatabase> => {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunkStore = db.createObjectStore(CHUNKS_STORE, {
          keyPath: "id",
        });
        chunkStore.createIndex("recordingId", "recordingId", { unique: false });
        chunkStore.createIndex("uploaded", "uploaded", { unique: false });
      }
      if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
        db.createObjectStore(SESSIONS_STORE, { keyPath: "recordingId" });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      attachDatabaseLifecycleHandlers(db);
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error("Failed to open IndexedDB"));
    };
  });

  return dbPromise;
};

const resetDatabaseConnectionForTests = (): void => {
  dbPromise = null;
};

const withStore = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> => {
  const db = await openDatabase();

  return new Promise<T | void>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = operation(store);

    transaction.oncomplete = () => {
      if (request instanceof IDBRequest) {
        resolve(request.result);
      } else {
        resolve();
      }
    };
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
  });
};

const chunkId = (recordingId: string, chunkIndex: number) =>
  `${recordingId}:${chunkIndex}`;

const saveChunk = async (
  recordingId: string,
  chunkIndex: number,
  blob: Blob
): Promise<void> => {
  const record: ChunkRecord = {
    id: chunkId(recordingId, chunkIndex),
    recordingId,
    chunkIndex,
    blob,
    uploaded: false,
    createdAt: Date.now(),
  };

  await withStore(CHUNKS_STORE, "readwrite", (store) => store.put(record));
};

const markChunksUploaded = async (
  recordingId: string,
  chunkIndices: number[]
): Promise<void> => {
  if (chunkIndices.length === 0) {
    return;
  }

  const db = await openDatabase();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(CHUNKS_STORE, "readwrite");
    const store = transaction.objectStore(CHUNKS_STORE);

    chunkIndices.forEach((chunkIndex) => {
      const request = store.get(chunkId(recordingId, chunkIndex));
      request.onsuccess = () => {
        const existing = request.result as ChunkRecord | undefined;
        if (existing && !existing.uploaded) {
          store.put({ ...existing, uploaded: true });
        }
      };
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Failed to mark chunks uploaded"));
  });
};

const markChunkUploaded = (
  recordingId: string,
  chunkIndex: number
): Promise<void> => markChunksUploaded(recordingId, [chunkIndex]);

const deleteChunk = async (
  recordingId: string,
  chunkIndex: number
): Promise<void> => {
  await withStore(CHUNKS_STORE, "readwrite", (store) =>
    store.delete(chunkId(recordingId, chunkIndex))
  );
};

const getPendingChunks = async (
  recordingId: string
): Promise<ChunkRecord[]> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(CHUNKS_STORE, "readonly");
    const store = transaction.objectStore(CHUNKS_STORE);
    const index = store.index("recordingId");
    const request = index.getAll(recordingId);

    request.onsuccess = () => {
      const chunks = (request.result as ChunkRecord[]).filter(
        (chunk) => !chunk.uploaded
      );
      resolve(chunks.sort((a, b) => a.chunkIndex - b.chunkIndex));
    };
    request.onerror = () =>
      reject(request.error ?? new Error("Failed to read pending chunks"));
  });
};

const saveSession = async (
  session: Omit<SessionRecord, "createdAt">
): Promise<void> => {
  const record: SessionRecord = { ...session, createdAt: Date.now() };
  await withStore(SESSIONS_STORE, "readwrite", (store) => store.put(record));
};

const getSession = async (
  recordingId: string
): Promise<SessionRecord | undefined> => {
  return (await withStore(SESSIONS_STORE, "readonly", (store) =>
    store.get(recordingId)
  )) as SessionRecord | undefined;
};

const clearSession = async (recordingId: string): Promise<void> => {
  const db = await openDatabase();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(
      [CHUNKS_STORE, SESSIONS_STORE],
      "readwrite"
    );
    const chunksStore = transaction.objectStore(CHUNKS_STORE);
    const sessionsStore = transaction.objectStore(SESSIONS_STORE);
    const index = chunksStore.index("recordingId");
    const request = index.openCursor(IDBKeyRange.only(recordingId));

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        chunksStore.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    sessionsStore.delete(recordingId);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Failed to clear session"));
  });
};

const isRecordExpired = (
  record: { createdAt?: number },
  cutoff: number
): boolean => typeof record.createdAt !== "number" || record.createdAt < cutoff;

const clearStaleRecordingData = async (
  maxAgeMs: number = RECORDING_DATA_MAX_AGE_MS,
  now: number = Date.now()
): Promise<void> => {
  const cutoff = now - maxAgeMs;
  const db = await openDatabase();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(
      [CHUNKS_STORE, SESSIONS_STORE],
      "readwrite"
    );

    const deleteExpiredIn = (store: IDBObjectStore) => {
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          return;
        }
        if (isRecordExpired(cursor.value as { createdAt?: number }, cutoff)) {
          cursor.delete();
        }
        cursor.continue();
      };
    };

    deleteExpiredIn(transaction.objectStore(CHUNKS_STORE));
    deleteExpiredIn(transaction.objectStore(SESSIONS_STORE));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(
        transaction.error ?? new Error("Failed to clear stale recording data")
      );
  });
};

export {
  clearSession,
  clearStaleRecordingData,
  deleteChunk,
  getPendingChunks,
  getSession,
  isRecordExpired,
  markChunkUploaded,
  markChunksUploaded,
  RECORDING_DATA_MAX_AGE_MS,
  resetDatabaseConnectionForTests,
  saveChunk,
  saveSession,
};
export type { ChunkRecord, SessionRecord };
